import { Input, PrimaryButton } from '@curie/react'
import { useEffect, useState } from 'react'
import { trace } from '~/services/analyticsService'
import { BetterToast } from '~/utils/toast'
import { sendVerificationCode, emailCodeVerification, emailverify } from '~/services/userService'
import VerificationInput from '../VerificationInput'
import { verifyEmail } from '~/utils/utils'

interface CampaignSignUpFormProps {
  onSuccess?: () => void
}

type SignupState = {
  email: string
}

type Step = 'signup' | 'verify'

export function CampaignSignUp({ onSuccess }: CampaignSignUpFormProps) {
  const [step, setStep] = useState<Step>('signup')
  const [formState, setFormState] = useState<SignupState>({ email: '' })
  const [verificationCode, setVerificationCode] = useState('')
  const [isDisabled, setIsDisabled] = useState(false)

  useEffect(() => {
    trace('signup_exposure')
  }, [])

  const handleInputChange = (field: keyof SignupState) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormState(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    trace('signup_click')
    const { email } = formState

    if (!email) {
      return BetterToast.error('Please enter your email')
    }

    if (!verifyEmail(email)) {
      return BetterToast.error('Invalid email address')
    }

    try {
      setIsDisabled(true)
      await sendVerificationCode(email)
      BetterToast.success('We\'ve sent you a code. Check your email!')
      setStep('verify')
    } catch (err) {
      BetterToast.error((err as Error).message)
    } finally {
      setIsDisabled(false)
    }
  }

  const handleVerificationSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!verificationCode) {
      return BetterToast.error('Please enter verification code')
    }

    try {
      setIsDisabled(true)
      await emailverify(formState.email, verificationCode)
      trace({ event: 'signup_completed', labels: { status: 'success' } })
      trace({ event: 'campaign_register_email', labels: { email: formState.email } })
      BetterToast.success('Your email is verified!')
      localStorage.setItem('registered', 'true')
      onSuccess?.()
    } catch (err) {
      BetterToast.error((err as Error).message)
      trace({ event: 'signup_completed', labels: { status: 'failed', reason: (err as Error).message } })
      setIsDisabled(false)
    }
  }

  const handleResendCode = async () => {
    try {
      setIsDisabled(true)
      await sendVerificationCode(formState.email)
      BetterToast.success('Verification code resent. Check your email!')
    } catch (err) {
      BetterToast.error((err as Error).message)
    } finally {
      setIsDisabled(false)
    }
  }

  const renderSignupForm = () => (
    <div className="flex-1 flex justify-center items-begin w-full h-full pb-4">
      <div className="w-full max-w-[450px] p-5 pt-4">
        <h1 className="mb-4 text-center font-semibold text-2xl">
          Get Access
        </h1>
        <p className="text-center text-gray-700 mb-6">
          Enter your email to try your own data.
        </p>
        <form className="space-y-6" onSubmit={handleSignUp}>
          <div>
            <Input
              type="email"
              placeholder="Your email"
              value={formState.email}
              onChange={handleInputChange('email')}
              autoComplete="email"
            />
          </div>
          <PrimaryButton
            type="submit"
            className="w-full rounded-3xl"
            isDisabled={isDisabled}
          >
            Continue
          </PrimaryButton>
        </form>
      </div>
    </div>
  )

  const renderVerificationForm = () => (
    <div className="flex-1 flex justify-center items-begin w-full h-full pb-4">
      <div className="w-full max-w-[450px] p-5 pt-4">
        <h1 className="mb-4 text-center font-semibold text-2xl">
          Check Your Email
        </h1>
        <p className="text-center text-gray-700 mb-6">
          We sent a verification code to <strong>{formState.email}</strong>.
        </p>
        <form className="space-y-4" onSubmit={handleVerificationSubmit}>
          <div className="flex justify-center">
            <VerificationInput
              value={verificationCode}
              onChange={setVerificationCode}
              disabled={isDisabled}
            />
          </div>
          <PrimaryButton
            type="submit"
            className="w-full rounded-3xl"
            isDisabled={isDisabled}
          >
            Verify
          </PrimaryButton>
        </form>
        <div className="text-center mt-2">
          <button
            type="button"
            onClick={handleResendCode}
            className="text-blue-600 hover:text-blue-800"
            disabled={isDisabled}
          >
            Resend code
          </button>
        </div>
      </div>
    </div>
  )

  return step === 'signup' ? renderSignupForm() : renderVerificationForm()
}

export default CampaignSignUp
